import Css from "./style.module.scss";

import * as Icons from "@phosphor-icons/react";
import Button from "nlib/ui/Button";
import React, { useCallback } from "react";
import classNames from "classnames";

const ONE_HALF = 0.5;

const DEFAULT_PAGE_SIZE = 20;

const DEFAULT_MAX_DISPLAYED_PAGES = 5;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const getPageCount = (count, pageSize) => {
  if (!count || count <= 0) return 0;

  return Math.ceil(count / pageSize);
};

const getVisiblePages = (currentPage, totalPages, maxDisplayedPages) => {
  if (totalPages <= 0) return [];

  const maxPages = Math.max(1, maxDisplayedPages);

  if (totalPages <= maxPages) {
    return Array.from({ length: totalPages }, (item, index) => index + 1);
  }

  const half = Math.floor(maxPages * ONE_HALF);

  let start = currentPage - half;

  let end = start + maxPages - 1;

  if (start < 1) {
    start = 1;
    end = maxPages;
  }
  if (end > totalPages) {
    end = totalPages;
    start = totalPages - maxPages + 1;
  }

  return Array.from({ length: maxPages }, (item, index) => start + index);
};

const Pagination = (props) => {
  const {
    className,
    page,
    count,
    pageSize = DEFAULT_PAGE_SIZE,
    maxDisplayedPages = DEFAULT_MAX_DISPLAYED_PAGES,
    showFirstLast = true,
    onChange
  } = props;

  const totalPages = getPageCount(count, pageSize);

  const safePage = clamp(page || 1, 1, Math.max(1, totalPages));

  const pages = getVisiblePages(safePage, totalPages, maxDisplayedPages);

  const disabled = props.disabled || totalPages <= 1;

  const handleItemClick = useCallback((event, nextPage) => {
    if (disabled || nextPage === safePage || nextPage < 1 || nextPage > totalPages) return;
    if (typeof onChange === "function") {
      onChange(nextPage);
    }
  }, [disabled, safePage, totalPages, onChange]);

  if (totalPages === 0) {
    return null;
  }

  return (
    <div className={classNames(Css.pagination, className)}>
      <div className={Css.pages}>
        {showFirstLast && (
          <Button
            light
            value={1}
            disabled={disabled || safePage === 1}
            className={Css.button}
            onClick={handleItemClick}>
            <Icons.CaretDoubleLeft />
          </Button>
        )}
        <Button
          light
          value={safePage - 1}
          disabled={disabled || safePage === 1}
          className={Css.button}
          onClick={handleItemClick}>
          <Icons.CaretLeft />
        </Button>
        {pages.map((item) => {
          const active = safePage === item;

          return (
            <Button
              className={Css.button}
              key={item}
              value={item}
              primary={active}
              light={!active}
              disabled={disabled || active}
              onClick={handleItemClick}>
              {item}
            </Button>
          );
        })}
        <Button
          light
          className={Css.button}
          value={safePage + 1}
          disabled={disabled || safePage === totalPages}
          onClick={handleItemClick}>
          <Icons.CaretRight />
        </Button>
        {showFirstLast && (
          <Button
            light
            className={Css.button}
            value={totalPages}
            disabled={disabled || safePage === totalPages}
            onClick={handleItemClick}>
            <Icons.CaretDoubleRight />
          </Button>
        )}
      </div>
    </div>
  );
};

export default React.memo(Pagination);
